import React, { useState, CSSProperties } from "react";
import { FormControl, Select, MenuItem, Tooltip } from "@mui/material";
import { SelectChangeEvent } from "@mui/material";
import { StatusFilterMenuItems } from "../../types/types";
import "./StatusFilter.css"

interface StatusFilterProps {
  onStatusChange: (status: string) => void;
  statusFilterMenuItems: StatusFilterMenuItems[];
  enableTruncation?: boolean;
}

const StatusFilter = ({ 
  onStatusChange, 
  statusFilterMenuItems, 
  enableTruncation = false 
}: StatusFilterProps) => {
  const [statusFilter, setStatusFilter] = useState(statusFilterMenuItems[0].id);

  const handleChange = (event: SelectChangeEvent) => {
    const selectedStatus = event.target.value;
    setStatusFilter(selectedStatus);
    onStatusChange(selectedStatus); // Notify parent component about the change
  };

  // Define styles based on whether truncation is enabled
  const selectedItemStyle: CSSProperties = enableTruncation ? {
    maxWidth: "160px",
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  } : {};

  const menuItemStyle: CSSProperties = enableTruncation ? {
    maxWidth: "180px",
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  } : {};

  return (
    <div>
      <FormControl>
        <Select
          labelId="dropdown_label"
          value={statusFilter}
          size="small"
          displayEmpty
          sx={{
            minWidth: "176px",
            maxWidth: enableTruncation ? "200px" : undefined,
            fontSize: "14px",
            border: "1px solid #DEDEDE",
            borderRadius: "8px",
            "&.Mui-focused": {
              border: "1px solid #DEDEDE",
              boxShadow: "none",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "& .MuiSelect-select": enableTruncation ? {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              paddingRight: "30px",
            } : {}
          }}
          onChange={handleChange}
          MenuProps={{
            PaperProps: {
              sx: {
                mt: 1,
                padding: 0,
                boxShadow: "none",
                border: "1px solid #DEDEDE",
                borderRadius: "8px",
                maxWidth: enableTruncation ? "200px" : undefined,
              },
            },
            MenuListProps: {
              sx: {
                padding: "7px 0",
              },
            },
          }}
          renderValue={(selected) => {
            if (!selected) {
              return (
                <span style={{ color: "#AFAFAF", fontSize: "12px" }}>
                  Filter by Status
                </span>
              );
            }
            const item = statusFilterMenuItems.find(
              (d) => d.id === selected
            );
            
            const content = (
              <div className="dropdown_selected_item" style={selectedItemStyle}>
                {item?.label ?? ""}
              </div>
            );
            
            return item ? (
              enableTruncation ? 
                <Tooltip title={item?.label ?? ""} placement="top" arrow>
                  {content}
                </Tooltip> : 
                content
            ) : null;
          }}
        >
          {statusFilterMenuItems?.map((item, index) => {
            const content = (
              <div className="status_filter_dropdown_menu_item" style={menuItemStyle}>
                <span>{item?.label ?? ""}</span>
              </div>
            );
            
            return (
              <MenuItem
                key={item.id}
                value={item.id}
                className="menuItem-test-debug"
                sx={{
                  padding: "2px 0px",
                  borderBottom:
                    index !== statusFilterMenuItems.length - 1
                      ? "1.5px dashed #EBEBEB"
                      : "none",
                  margin: "0 7px",
                }}
              >
                {enableTruncation ? 
                  <Tooltip title={item?.label ?? ""} placement="right" arrow>
                    {content}
                  </Tooltip> : 
                  content
                }
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </div>
  );
};

export default StatusFilter;